/**
 * Custom hook for My Account data — powered by TanStack Query
 *
 * Server state (account, dashboard, invoices, ledger) is managed by useQuery.
 * Derived state (billingData, usageData, accountData) is computed from query
 * results in useMemo — no more cascading setState chains.
 * UI state (modal visibility, selected plan) stays in useState.
 * Mutations (purchase, edit) use useMutation with automatic cache invalidation.
 *
 * Pure derivation logic lives in useMyAccountData.helpers.ts for testability.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { BILLING_PLANS, BillingPlanKey } from '../config/billingPlans';
import { useCognito } from '../contexts/CognitoProvider';
import { useWebSocket } from '../contexts/WebSocketProvider';
import { fetchDashboardData, fetchInvoices, fetchMyAccount, fetchUsageLedger } from '../services/configService';
import socketService from '../services/socketService';
import wsApi from '../services/wsApi';
import useInvitationsStore from '../stores/invitationsStore';
import {
  deriveAccountData,
  deriveBillingData,
  deriveCurrentUser,
  deriveSelectedPlan,
  deriveUsageData,
  mergeCreditOverride
} from './useMyAccountData.helpers';
import type { AccountData, Invoice, LedgerTransaction } from './useMyAccountData.types';
import { EMPTY_ACCOUNT } from './useMyAccountData.types';
import { useToast } from './useToast';

// Re-export types for consumers
export { EMPTY_ACCOUNT } from './useMyAccountData.types';
export type { AccountData, BillingData, CurrentUser, Invoice, LedgerTransaction, UsageData } from './useMyAccountData.types';

// ── Query keys ──────────────────────────────────────────────────────────────
export const accountKeys = {
  all: ['account'] as const,
  myAccount: ['account', 'myAccount'] as const,
  dashboard: ['account', 'dashboard'] as const,
  invoices: ['account', 'invoices'] as const,
  ledger: ['account', 'ledger'] as const,
};

// ── Hook ────────────────────────────────────────────────────────────────────

export const useMyAccountData = () => {
  const queryClient = useQueryClient();
  const { toast, showToast, closeToast } = useToast();
  const { isAuthenticated, user: cognitoUser } = useCognito();
  const { isAuthenticated: wsAuthenticated } = useWebSocket();
  const storeInvitations = useInvitationsStore((s) => s.invitations);

  const isReady = isAuthenticated && wsAuthenticated;

  // ── UI state (unchanged) ──────────────────────────────────────────────
  const [showChangePlanModal, setShowChangePlanModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showInvoicesModal, setShowInvoicesModal] = useState(false);
  const [showEditAccountModal, setShowEditAccountModal] = useState(false);
  const [showEditAdminModal, setShowEditAdminModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('GROWTH');
  const [editAccountData, setEditAccountData] = useState<AccountData>(EMPTY_ACCOUNT);

  // ── Queries ───────────────────────────────────────────────────────────
  const myAccountQuery = useQuery({
    queryKey: accountKeys.myAccount,
    queryFn: () => fetchMyAccount().catch(() => ({ data: null, source: 'real' as const })),
    enabled: isReady,
  });

  const dashboardQuery = useQuery({
    queryKey: accountKeys.dashboard,
    queryFn: () => fetchDashboardData().catch(() => ({ data: null, source: 'real' as const })),
    enabled: isReady,
  });

  // Invoices are fetched on demand: the QueryInvoices request is only sent to
  // the backend when the user opens the Invoices modal, and re-sent on each open
  // so the list is always fresh.
  const invoicesQuery = useQuery({
    queryKey: accountKeys.invoices,
    queryFn: () => fetchInvoices().catch(() => [] as any[]),
    enabled: isReady && showInvoicesModal,
  });

  const ledgerQuery = useQuery({
    queryKey: accountKeys.ledger,
    queryFn: () => fetchUsageLedger().catch(() => ({ transactions: [], credits: null })),
    enabled: isReady,
  });

  // ── Derived server state (pure computation, no setState) ──────────────
  const myAccData = myAccountQuery.data?.data ?? null;
  const dashData = dashboardQuery.data?.data ?? null;

  const derivedAccount = useMemo(
    () => deriveAccountData(myAccData, dashData, cognitoUser),
    [myAccData, dashData, cognitoUser],
  );

  const currentUser = useMemo(
    () => deriveCurrentUser(myAccData, cognitoUser),
    [myAccData, cognitoUser],
  );

  const billingData = useMemo(
    () => deriveBillingData(myAccData, dashData),
    [myAccData, dashData],
  );

  const derivedSelectedPlan = useMemo(
    () => deriveSelectedPlan(myAccData, dashData),
    [myAccData, dashData],
  );

  const usageData = useMemo(
    () => deriveUsageData(myAccData, dashData, ledgerQuery.data ?? null, storeInvitations),
    [myAccData, dashData, ledgerQuery.data, storeInvitations],
  );

  const invoices: Invoice[] = useMemo(
    () => (invoicesQuery.data?.length ? invoicesQuery.data : []),
    [invoicesQuery.data],
  );

  const ledger: LedgerTransaction[] = useMemo(
    () => ledgerQuery.data?.transactions || [],
    [ledgerQuery.data],
  );

  // ── Sync derived account into editable local state ────────────────────
  useEffect(() => {
    if (derivedAccount.accountId) {
      setEditAccountData(derivedAccount);
    }
  }, [derivedAccount]);

  useEffect(() => {
    if (derivedSelectedPlan) setSelectedPlan(derivedSelectedPlan);
  }, [derivedSelectedPlan]);

  const accountData: AccountData = editAccountData.accountId ? editAccountData : derivedAccount;

  // ── Loading / credit-notification override ────────────────────────────
  const isLoading = myAccountQuery.isLoading || dashboardQuery.isLoading;

  // Real-time credit notification → update query cache directly
  const [creditOverride, setCreditOverride] = useState<number | null>(null);

  useEffect(() => {
    const handler = (notification: any) => {
      if (!notification) return;
      const remaining = notification.creditsRemaining;
      if (remaining != null) {
        setCreditOverride(remaining);
      }
      if (notification.severity === 'critical') {
        showToast('⚠️ You have no credits remaining. Top up to continue.', 'error');
      } else if (notification.severity === 'warning') {
        showToast(`⚠️ Low credits: ${remaining} remaining`, 'error');
      }
    };
    socketService.on('credit:notification', handler);
    return () => { socketService.off('credit:notification', handler); };
  }, [showToast]);

  // Merge credit override into usageData
  const finalUsageData = useMemo(
    () => mergeCreditOverride(usageData, creditOverride),
    [usageData, creditOverride],
  );

  // ── Refresh helper (invalidates all account queries) ──────────────────
  const refreshAccountData = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: accountKeys.all });
  }, [queryClient]);

  // Open the Invoices modal and send a fresh QueryInvoices request to the
  // backend. Invalidation forces a refetch even when a cached list exists, so
  // the modal always reflects the latest invoices/invoice numbers.
  const openInvoicesModal = useCallback(() => {
    setShowInvoicesModal(true);
    queryClient.invalidateQueries({ queryKey: accountKeys.invoices });
  }, [queryClient]);

  // ── Handlers ──────────────────────────────────────────────────────────
  const handleChangePlan = useCallback(async () => {
    const plan = BILLING_PLANS[selectedPlan as BillingPlanKey];
    if (!plan) {
      showToast('Please select a valid bundle', 'error');
      return;
    }

    try {
      const result = await wsApi.rechargePurchase(selectedPlan, plan.credits, plan.price);
      if (result.success) {
        showToast(`✓ ${plan.credits.toLocaleString()} credits purchased successfully!`, 'success');
        if (result.newTotal != null) setCreditOverride(result.newTotal);
        refreshAccountData();
      } else {
        showToast('Purchase could not be completed. Please try again.', 'error');
      }
    } catch (error) {
      showToast('Failed to process purchase. Please try again later.', 'error');
    }
    setShowChangePlanModal(false);
  }, [selectedPlan, showToast, refreshAccountData, setCreditOverride]);

  const handleUpdatePayment = useCallback(() => {
    showToast('✓ Payment method updated successfully', 'success');
    setShowPaymentModal(false);
  }, [showToast]);

  const handleEditAccount = useCallback(() => {
    showToast('✓ Account details updated successfully', 'success');
    setShowEditAccountModal(false);
  }, [showToast]);

  const handlePaymentSuccess = useCallback((creditsAdded: number) => {
    showToast(`${creditsAdded.toLocaleString()} credits purchased successfully!`, 'success');
    const current = finalUsageData?.creditsRemaining ?? creditOverride ?? 0;
    setCreditOverride(current + creditsAdded);
    refreshAccountData();
  }, [showToast, refreshAccountData, finalUsageData, creditOverride, setCreditOverride]);

  const handleEditAdmin = useCallback(() => {
    showToast('✓ Admin contact details updated successfully', 'success');
    setShowEditAdminModal(false);
  }, [showToast]);
  return {
    accountData,
    currentUser,
    billingData,
    usageData: finalUsageData,
    invoices,
    ledger,
    isLoading,
    toast,
    closeToast,
    showChangePlanModal,
    setShowChangePlanModal,
    showPaymentModal,
    setShowPaymentModal,
    showInvoicesModal,
    setShowInvoicesModal,
    openInvoicesModal,
    invoicesLoading: invoicesQuery.isFetching,
    showEditAccountModal,
    setShowEditAccountModal,
    showEditAdminModal,
    setShowEditAdminModal,
    selectedPlan,
    setSelectedPlan,
    editAccountData,
    setEditAccountData,
    handleChangePlan,
    handlePaymentSuccess,
    handleUpdatePayment,
    handleEditAccount,
    handleEditAdmin,
  };
};
